import React, { useEffect, useState } from "react";
import { Card, CardContent, Typography } from "@material-ui/core";
import BarChart from "../../shared/components/charts/BarChart";
import LineChart from "../../shared/components/charts/LineChart";
import ApiService from "../../service/ApiService";
import cardStyles from "../styles/cardStyles";
import useStyles from "./styles";
import { Skeleton } from "@mui/material";
import { Spinner } from "../../components";
import AlertService from "../../service/AlertService";
import ToastMessages from "../../model/enum/ToastMessages";
import { ToastContainer } from "react-toastify";
import StatsResponse from "../../model/statsResponse";

const Labels = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "June",
  "July",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const Stats = () => {
  const classes = useStyles();
  const styles = cardStyles();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const yearList = Array.from(
    { length: new Date().getFullYear() - 2023 + 1 },
    (_, i) => 2023 + i,
  );
  const [graphType, setGraphType] = useState("Bar");
  const graphTypes = ["Bar", "Line"];

  // Fetch all
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const prevYear = year - 1;
        const [
          appointmentsData,
          requestsData,
          contactsData,
          prevAppointments,
          prevRequests,
          prevContacts,
        ] = await Promise.all([
          ApiService.get<StatsResponse[]>(`stats/appointments?year=${year}`),
          ApiService.get<StatsResponse[]>(`stats/requests?year=${year}`),
          ApiService.get<StatsResponse[]>(`stats/messages?year=${year}`),
          ApiService.get<StatsResponse[]>(
            `stats/appointments?year=${prevYear}`,
          ),
          ApiService.get<StatsResponse[]>(`stats/requests?year=${prevYear}`),
          ApiService.get<StatsResponse[]>(`stats/messages?year=${prevYear}`),
        ]);
        // prepare arrays for chart and percent labels (compare same month last year)
        const toArray = (arr: any[]) =>
          arr.flat().map((d: any) => d.count || 0);
        const apptArr = toArray(appointmentsData);
        const reqArr = toArray(requestsData);
        const msgArr = toArray(contactsData);
        const prevApptArr = toArray(prevAppointments);
        const prevReqArr = toArray(prevRequests);
        const prevMsgArr = toArray(prevContacts);

        const currentYear = new Date().getFullYear();
        const currentMonthIndex = new Date().getMonth(); // 0-based
        // compute month-over-month percent change for each dataset, return null for months with no prior-year comparison
        const yoy = (currArr: number[], prevArr: number[]) =>
          currArr.map((curr, idx) => {
            // if selected year is the current year, only compute up to the current month
            if (year === currentYear && idx > currentMonthIndex) return null;
            const prev =
              prevArr && prevArr[idx] !== undefined ? prevArr[idx] : null;
            if (prev === null || prev === 0) return null; // no comparison available
            return Number((((curr - prev) / prev) * 100).toFixed(1));
          });

        setData({
          labels: Labels,
          datasets: [
            {
              label: "Appointments",
              data: apptArr,
              monthly: yoy(apptArr, prevApptArr),
              backgroundColor: "rgba(24, 47, 88, 1)",
            },
            {
              label: "Messages",
              data: msgArr,
              monthly: yoy(msgArr, prevMsgArr),
              backgroundColor: "rgba(36, 71, 133, 1)",
            },
            {
              label: "Requests",
              data: reqArr,
              monthly: yoy(reqArr, prevReqArr),
              backgroundColor: "rgba(56, 110, 207, 1)",
            },
          ],
        });
      } catch (error) {
        AlertService.error(ToastMessages.FETCH_FAILED);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [year]);

  const handleYearChange = (e: any) => {
    setLoading(true);
    setData({});
    setYear((_year) => parseInt(e.target.value));
  };

  const handleTypeChange = (e: any) => setGraphType(e.target.value);

  const Graph = () => {
    if (Object.keys(data).length === 0) {
      return <Typography>No data available for the selected year.</Typography>;
    }

    switch (graphType) {
      case "Bar":
        return <BarChart chartData={data} />;
      case "Line":
        return <LineChart chartData={data} />;
    }

    return <></>;
  };

  return (
    <>
      <Card raised className={styles.card}>
        {loading ? (
          <>
            <Spinner />
            <Skeleton style={{ height: "30rem" }} />
          </>
        ) : (
          <>
            <div className={styles.headerContainer}>
              <Typography className={styles.title}>Statistics</Typography>
              <div>
                <select
                  value={graphType}
                  className={classes.selector}
                  onChange={handleTypeChange}
                >
                  {graphTypes.map((graph: string) => (
                    <option>{graph}</option>
                  ))}
                </select>{" "}
                <select
                  className={classes.selector}
                  onChange={handleYearChange}
                  value={year}
                >
                  {yearList.map((y: number) => (
                    <option>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            <CardContent className={classes.statsCardContent}>
              <Typography
                style={{ fontSize: 12, color: "#666", marginBottom: 8 }}
              >
                Month-over-month percent labels are displayed on the chart for
                months with prior-month data.
              </Typography>
              <Graph />
            </CardContent>
          </>
        )}
      </Card>
      <ToastContainer />
    </>
  );
};

export default Stats;
